import React from 'react'
import { Toast, type ToastKind } from '@/components/ds'

interface ToastItem {
  id: number
  kind: ToastKind
  title: string
  detail?: string
}

type ToastFn = (kind: ToastKind, title: string, detail?: string) => void

const ToastContext = React.createContext<ToastFn | null>(null)

let nextId = 1

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastItem[]>([])

  const dismiss = React.useCallback((id: number) => {
    setToasts((ts) => ts.filter((t) => t.id !== id))
  }, [])

  const toast = React.useCallback<ToastFn>(
    (kind, title, detail) => {
      const id = nextId++
      setToasts((ts) => [...ts, { id, kind, title, detail }])
      // ~3s for everything. Errors used to linger for 8s, which is most of the
      // time a modal is open — and a lingering toast is exactly what ends up
      // parked over the modal's buttons.
      window.setTimeout(() => dismiss(id), kind === 'error' ? 4500 : 3000)
    },
    [dismiss],
  )

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {/* Top centre, z-index 1000 — above Dialog's 100, so a toast is never
          resting on a modal's footer buttons. Styles in src/index.css. */}
      <div className="lm-toasts">
        {toasts.map((t) => (
          <Toast
            key={t.id}
            kind={t.kind}
            title={t.title}
            detail={t.detail}
            onDismiss={() => dismiss(t.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastFn {
  const ctx = React.useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx
}
